/**
 * QR Service — QR code generation and scanning.
 *
 * Generation: data URL via `qrcode` library, standard dark-on-light colors.
 * Scanning: Native BarcodeDetector API (Chrome 83+, Safari 17.2+) with
 *           qr-scanner library as fallback for older browsers.
 */

import QRCode from 'qrcode';

export async function generateQrDataUrl(data: string, size: number = 280): Promise<string> {
  return QRCode.toDataURL(data, {
    width: size,
    margin: 3,
    errorCorrectionLevel: 'H',
    color: {
      dark: '#000000',
      light: '#ffffff',
    },
  });
}

export async function generateQrSvg(data: string): Promise<string> {
  return QRCode.toString(data, {
    type: 'svg',
    margin: 3,
    errorCorrectionLevel: 'H',
    color: {
      dark: '#000000',
      light: '#ffffff',
    },
  });
}

export interface QrScannerHandle {
  start(): Promise<void>;
  stop(): void;
  destroy(): void;
}

/**
 * Create a QR scanner using the best available method:
 * 1. Native BarcodeDetector API (fast, no worker needed)
 * 2. qr-scanner library fallback (uses wasm worker)
 */
export function createQrScanner(
  videoElement: HTMLVideoElement,
  onResult: (data: string) => void,
  onError?: (error: Error) => void
): QrScannerHandle {
  if ('BarcodeDetector' in window) {
    return createNativeBarcodeScanner(videoElement, onResult, onError);
  }
  return createQrScannerLibFallback(videoElement, onResult, onError);
}

function createNativeBarcodeScanner(
  videoElement: HTMLVideoElement,
  onResult: (data: string) => void,
  onError?: (error: Error) => void
): QrScannerHandle {
  let stream: MediaStream | null = null;
  let animFrameId: number = 0;
  let running = false;
  let lastResult = '';
  let lastResultTime = 0;
  const DEBOUNCE_MS = 2000;

  const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });

  const scan = async () => {
    if (!running || videoElement.readyState < 2) {
      if (running) animFrameId = requestAnimationFrame(() => void scan());
      return;
    }
    try {
      const barcodes = await detector.detect(videoElement);
      for (const barcode of barcodes) {
        const now = Date.now();
        if (barcode.rawValue === lastResult && now - lastResultTime < DEBOUNCE_MS) {
          continue;
        }
        lastResult = barcode.rawValue;
        lastResultTime = now;
        onResult(barcode.rawValue);
        return;
      }
    } catch {
      // Frame not ready
    }
    if (running) {
      animFrameId = requestAnimationFrame(() => void scan());
    }
  };

  return {
    async start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        videoElement.srcObject = stream;
        videoElement.setAttribute('playsinline', 'true');
        await videoElement.play();
        running = true;
        animFrameId = requestAnimationFrame(() => void scan());
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error('Camera access failed'));
      }
    },
    stop() {
      running = false;
      cancelAnimationFrame(animFrameId);
      stream?.getTracks().forEach(t => t.stop());
      videoElement.srcObject = null;
    },
    destroy() {
      this.stop();
    },
  };
}

function createQrScannerLibFallback(
  videoElement: HTMLVideoElement,
  onResult: (data: string) => void,
  onError?: (error: Error) => void
): QrScannerHandle {
  let scanner: any = null;
  let lastResult = '';
  let lastResultTime = 0;
  const DEBOUNCE_MS = 2000;

  return {
    async start() {
      try {
        const QrScannerModule = await import('qr-scanner');
        const QrScanner = QrScannerModule.default;
        scanner = new QrScanner(
          videoElement,
          (result: { data: string }) => {
            const now = Date.now();
            if (result.data === lastResult && now - lastResultTime < DEBOUNCE_MS) return;
            lastResult = result.data;
            lastResultTime = now;
            onResult(result.data);
          },
          {
            preferredCamera: 'environment',
            highlightScanRegion: true,
            highlightCodeOutline: true,
            returnDetailedScanResult: true,
          }
        );
        await scanner.start();
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error('QR scanner start failed'));
      }
    },
    stop() {
      scanner?.stop();
    },
    destroy() {
      scanner?.destroy();
      scanner = null;
    },
  };
}

export async function isQrScannerSupported(): Promise<boolean> {
  if ('BarcodeDetector' in window) {
    try {
      const formats = await (window as any).BarcodeDetector.getSupportedFormats();
      if (formats.includes('qr_code')) return true;
    } catch { /* fall through */ }
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(d => d.kind === 'videoinput');
  } catch {
    return false;
  }
}
